import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import NativeDocumentScanner from "@/plugins/nativeDocumentScanner";

export interface UseNativeScannerResult {
  platform: string;
  isNativePlatform: boolean;
  isNativeAvailable: boolean;
  isLoading: boolean;
  lastError: string | null;
  recheckAvailability: () => Promise<void>;
  scanDocument: () => Promise<{ base64Images: string[]; pageCount: number } | null>;
}

export function useNativeScanner(): UseNativeScannerResult {
  const [platform] = useState(() => Capacitor.getPlatform());
  const [isNativePlatform] = useState(() => {
    const maybeFn = (Capacitor as any).isNativePlatform;
    return typeof maybeFn === "function" ? Boolean(maybeFn()) : platform !== "web";
  });

  const [isNativeAvailable, setIsNativeAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastError, setLastError] = useState<string | null>(null);

  const recheckAvailability = useCallback(async () => {
    setIsLoading(true);
    setLastError(null);

    try {
      // Only check on native iOS platform
      if (isNativePlatform && platform === "ios") {
        const result = await NativeDocumentScanner.isAvailable();
        setIsNativeAvailable(Boolean(result.available));
      } else {
        setIsNativeAvailable(false);
      }
    } catch (error: any) {
      const message =
        error?.message ||
        (typeof error === "string" ? error : JSON.stringify(error, null, 2));
      setLastError(message);
      setIsNativeAvailable(false);
    } finally {
      setIsLoading(false);
    }
  }, [isNativePlatform, platform]);

  useEffect(() => {
    recheckAvailability();
  }, [recheckAvailability]);

  const scanDocument = useCallback(async () => {
    if (!isNativeAvailable) return null;

    try {
      const result = await NativeDocumentScanner.scanDocument();
      return {
        base64Images: result.images.map((img) => `data:image/jpeg;base64,${img.base64}`),
        pageCount: result.pageCount,
      };
    } catch (error: any) {
      // User cancelled or error
      const message = error?.message || "";
      if (message.toLowerCase().includes("cancel")) return null;
      throw error;
    }
  }, [isNativeAvailable]);

  return {
    platform,
    isNativePlatform,
    isNativeAvailable,
    isLoading,
    lastError,
    recheckAvailability,
    scanDocument,
  };
}

